import { useMutation, useQuery } from "@apollo/client";
import { css } from "@emotion/css";
import { CURRENT_USER, GOOGLE_OAUTH_QUERY, UPDATE_SERVICES } from "apollo";
import {
  InfoAlertDialog,
  MultiplayerServicesLayout,
  PageLayout,
  Separator,
  Text,
  ToastMessage,
} from "components";
import { GmailIcon, GoogleCalendarIcon, ZoomIcon } from "components/icons";
import { useSubscriptionPlan } from "hooks";
import { useCallback, useEffect, useState } from "react";
import {
  GetMeResponse,
  GoogleOauthQueryDto,
  ServiceName,
  T_Integration,
} from "types";
import { E_Route, MultiplayerServices } from "utils/constants";
import { IntegrationItem, RemoveDialog } from "../components";
import { IntegrationStyle } from "../styles/integration";
import { authFlowInterface } from "../utils";
import axios from "axios";

export const IntegrationScreen: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [integration, setIntegration] = useState<T_Integration>();
  const [showToast, setShowToast] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [zoomAuthUrl, setZoomAuthUrl] = useState("");

  const { isFree } = useSubscriptionPlan();

  const { data: userData, refetch: refetchUser } = useQuery<GetMeResponse>(
    CURRENT_USER,
    {
      fetchPolicy: "network-only",
    },
  );

  const { data: calendarData } = useQuery<any, GoogleOauthQueryDto>(
    GOOGLE_OAUTH_QUERY,
    {
      variables: {
        oauthData: {
          redirect: `${process.env.REACT_APP_HOST_URL}${E_Route.Done}`,
          type: "calendar",
          scopes: [
            "https://www.googleapis.com/auth/calendar.events",
            "https://www.googleapis.com/auth/calendar.readonly",
            "https://www.googleapis.com/auth/userinfo.email",
          ],
        },
      },
    },
  );

  const { data: gmailData } = useQuery<any, GoogleOauthQueryDto>(
    GOOGLE_OAUTH_QUERY,
    {
      variables: {
        oauthData: {
          redirect: `${process.env.REACT_APP_HOST_URL}${E_Route.Done}`,
          type: "gmail",
          scopes: [
            "https://www.googleapis.com/auth/gmail.modify",
            "https://www.googleapis.com/auth/contacts.readonly",
            "https://www.googleapis.com/auth/contacts.other.readonly",
            "https://www.googleapis.com/auth/userinfo.email",
          ],
        },
      },
    },
  );

  useEffect(() => {
    async function getZoomAuthUrl() {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/zoom/oauth?userId=${userData?.me?._id}&redirect=${process.env.REACT_APP_HOST_URL}${E_Route.Done}`,
      );
      if (res.data) {
        setZoomAuthUrl(res.data as string);
      }
    }
    if (userData?.me?._id) {
      getZoomAuthUrl();
    }
  }, [userData?.me?._id]);

  const [updateServices] = useMutation(UPDATE_SERVICES);

  const onGmailAuth = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    // auth(authData?.gmailOAuthUrl!, e, 'gmail');
    authFlowInterface(gmailData?.googleOAuthUrl!, e, () => {
      refetchUser();
    });
  };
  const onCalendarAuth = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (isFree) {
        setShowAlert(true);
        setAlertMessage(
          "Calendar integration is only available to paid users. Please upgrade your plan to enable this feature.",
        );
        return;
      }
      // auth(authData?.calendarOAuthUrl!, e, 'calendar');
      authFlowInterface(calendarData?.googleOAuthUrl!, e, () => {
        refetchUser();
      });
    },
    [calendarData?.googleOAuthUrl, isFree, refetchUser],
  );

  const onZoomAuth = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (isFree) {
      setShowAlert(true);
      setAlertMessage(
        "Zoom integration is only available to paid users. Please upgrade your plan to enable this feature.",
      );
      return;
    }
    if (!userData?.me?.hasCalendar) {
      setShowAlert(true);
      setAlertMessage(
        "Tip: Connect also you Calendar and Multiplayer AI will automatically join your meetings to take notes for you.",
      );
    }

    authFlowInterface(zoomAuthUrl!, e, () => {
      refetchUser();
    });
  };

  useEffect(() => {
    const cal = window.location.href?.split("#")[1];
    if (!userData?.me?.hasCalendar && cal === "calendar") {
      document.getElementById("calendar")?.scrollIntoView();
      onCalendarAuth({} as React.MouseEvent<HTMLDivElement, MouseEvent>);
    }
  }, [userData?.me?.hasCalendar, onCalendarAuth]);

  const onRemoveAuth = (type: T_Integration) => () => {
    setIntegration(type);
    setDialogOpen(true);
  };

  const handleCloseDialog = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      refetchUser().then(() => {
        //const hasCalendar = userData?.me?.hasCalendar;
        //const hasGmail = userData?.me?.hasGmail;
        //let newServices = userData?.me?.services;
        //if (!hasCalendar) {
        //  // deactivate related services
        //  newServices = newServices?.filter(
        //    (s) =>
        //      ![ServiceName.MeetingAssistant, ServiceName.MeetingPrep].includes(
        //        s,
        //      ),
        //  );
        //}
        //if (!hasGmail) {
        //  newServices = newServices?.filter(
        //    (s) =>
        //      ![ServiceName.InboxManagement, ServiceName.DraftReply].includes(
        //        s,
        //      ),
        //  );
        //}
        //if (!hasGmail && !hasCalendar) {
        //  newServices = newServices?.filter(
        //    (s) => ![ServiceName.DailyPodcast].includes(s),
        //  );
        //}
        //updateServices({
        //  variables: {
        //    services: newServices,
        //  },
        //}).then(() => {
        //  refetchUser();
        //});
      });
    }
  };

  const [selectedServices, setSelectedServices] = useState<ServiceName[]>(
    MultiplayerServices.map((s) => s.id),
  );

  useEffect(() => {
    setSelectedServices(
      userData?.me?.services || MultiplayerServices.map((s) => s.id),
    );
  }, [userData?.me?.services]);

  const handleServiceSelected = (service: ServiceName, selected: boolean) => {
    if (selected) {
      const serviceScopes =
        MultiplayerServices.find((s) => s.id === service)?.scopes || [];
      const hasCalendar = userData?.me?.hasCalendar;
      const hasGmail = userData?.me?.hasGmail;
      const e = {} as React.MouseEvent<HTMLDivElement, MouseEvent>;
      if (
        serviceScopes.some((scope) => scope.includes("calendar")) &&
        !hasCalendar
      ) {
        alert(
          "You will be redirected to connect your Calendar account first. Then you can enable this service.",
        );

        authFlowInterface(calendarData?.googleOAuthUrl!, e, () => {
          refetchUser();
        });
        return;
      }
      if (serviceScopes.some((scope) => scope.includes("gmail")) && !hasGmail) {
        alert(
          "You will be redirected to connect your Gmail account first. Then you can enable this service.",
        );
        authFlowInterface(gmailData?.googleOAuthUrl!, e, () => {
          refetchUser();
        });
        return;
      }
    }

    const currentServices = [...selectedServices];
    if (selected) {
      setSelectedServices((prev) => [...prev, service]);
      currentServices.push(service);
    } else {
      setSelectedServices((prev) => prev?.filter((s) => s !== service));
      currentServices.splice(currentServices.indexOf(service), 1);
    }
    updateServices({
      variables: {
        services: currentServices,
      },
    }).then(() => {
      setShowToast(true);
    });
  };

  return (
    <PageLayout title="Multiplayer services | Multiplayer">
      <InfoAlertDialog
        open={showAlert}
        message={alertMessage}
        onOpenChange={setShowAlert}
      />
      <div className={IntegrationStyle}>
        <ToastMessage
          show={showToast}
          handleShow={setShowToast}
          success
          duration={3000}
        />
        <Text.H2>Multiplayer services</Text.H2>
        <MultiplayerServicesLayout
          onSelected={handleServiceSelected}
          selectedServices={selectedServices}
        />

        <Separator.Horizontal spacing={10} />
        <Text.H2>Connected accounts:</Text.H2>

        <div
          className={css`
            border: 1px solid #272539;
            width: 100%;
            border-radius: 8px;
            padding: 8px 8px;
            margin-top: 16px;
          `}
        >
          <IntegrationItem
            title="Gmail"
            description="Connect your Gmail account and let Multiplayer manage your inbox"
            Icon={GmailIcon}
            completed={!!userData?.me?.hasGmail}
            onClick={onGmailAuth}
            onRemove={onRemoveAuth("gmail")}
            isIntegration
          />
        </div>
        <div
          id="calendar"
          className={css`
            border: 1px solid #272539;
            width: 100%;
            border-radius: 8px;
            padding: 8px 8px;
            margin-top: 16px;
          `}
        >
          <IntegrationItem
            title="Calendar"
            description="Connect your Calendar account and always be ready for your next meeting"
            Icon={GoogleCalendarIcon}
            completed={!!userData?.me?.hasCalendar}
            onClick={onCalendarAuth}
            onRemove={onRemoveAuth("calendar")}
            isIntegration
          />
        </div>

        <div
          className={css`
            border: 1px solid #272539;
            width: 100%;
            border-radius: 8px;
            padding: 8px 8px;
            margin-top: 16px;
          `}
        >
          <IntegrationItem
            title="Zoom"
            description="Connect your Zoom account and have Multiplayer AI take notes for you on each Zoom call"
            Icon={ZoomIcon}
            completed={!!userData?.me?.hasZoom}
            onClick={onZoomAuth}
            onRemove={onRemoveAuth("zoom")}
            isIntegration
          />
        </div>

        <RemoveDialog
          open={dialogOpen}
          type={integration!}
          integrationId={userData?.me?.email || ""}
          onOpenChange={handleCloseDialog}
        />
      </div>
    </PageLayout>
  );
};
